import React, { useEffect, useState } from "react";
import { Search} from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { selectModuleTabs } from "../../../redux/selectors/tabsSelectors";
import { addTab, removeTab, setActiveTab } from "../../../redux/slices/documentUploaderSlice";

// UI & utility imports
import FilterButton from "../../../components/ACS-Dashboard/ACS-Blocks/FilterButton";
import Button from "../../../components/ACS-Dashboard/ACS-Blocks/Button";
import Tabs from "../../../components/ACS-Dashboard/ACS-Blocks/Tabs";

// Tab components
import DocumentUploadTable from "./TabContent/DocumentTable";
import NewDocumentUpload from "./TabContent/NewDocumentUpload";

const DocumentUploaderDetails = () => {
  const dispatch = useDispatch();
  const { tabs, activeTab, defaultTabsCount } = useSelector(
    selectModuleTabs("documentUploader") // ✅ Corrected
  );

  const [totalDocuments, setTotalDocuments] = useState(0); // ✅ Corrected casing
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(false);
  const [filter, setFilter] = useState(null);

  // Log Redux tabs for debugging
  useEffect(() => {
    console.log("Tabs:", tabs);
  }, [tabs]);

  const handleFilter = (selectedValue) => {
    setFilter(selectedValue === "All Statuses" ? null : selectedValue);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setFilter(null);
    setSearchTrigger((prev) => !prev);
  };

  // Default content for each tab
  const defaultTabContents = {
    "DocumentUploader": ( 
      <DocumentUploadTable
        key="DocumentUploader"
        filter={filter}
        searchTrigger={searchTrigger}
        searchQuery={searchQuery}
        setTotalDocuments={setTotalDocuments} 
      />
    ),
  };

  // When clicking "Upload Document", add a new tab
  const handleCreateDocument = () => { 
    dispatch(addTab());
  };

  const handleRemoveTab = (index) => {
    if (index < defaultTabsCount) {
      alert("Default tabs cannot be removed.");
      return;
    }
    dispatch(removeTab({ tabIndex: index }));
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) return;

    const newTabId = `search-${Date.now()}`;
    dispatch(addTab({
      id: newTabId,
      name: `Search: ${searchQuery}`,
      contentType: "searchResults",
      searchQuery,
      isDynamic: true,
      createdAt: Date.now(),
    }));
    dispatch(setActiveTab({ index: tabs.length }));
  };

  return (
    <div className="p-fluid-1 shadow-md flex flex-col flex-1 h-full min-h-0 overflow-hidden">
      {/* Top Action/Filter Bar */}
      <div className="flex flex-wrap justify-between items-center rounded-md ml-[2] p-fluid-1">
        {/* Left: Filters and Search */}
        <div className="flex flex-wrap items-center gap-2">
          <FilterButton text="Filter" backgroundColor="white" textColor="black" />
          <FilterButton text="Custom Filters" backgroundColor="white" textColor="black" />

          {/* Search Box */}
          <div className="relative flex items-center border border-gray-300 bg-white rounded-full px-3 py-1.5 w-80">
            <input
              type="text"
              placeholder="Search document"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
              className="w-full outline-none text-fluid-sm text-gray-800"
            />
            <button
              onClick={handleSearch}
              className="absolute right-3 text-gray-500 hover:text-gray-700"
            >
              <Search size={18} strokeWidth={2} />
            </button>
          </div>

          <span className="text-gray-500 text-fluid-sm">
            {totalDocuments} documents
          </span>
        </div>

        {/* Right: Download & Upload */}
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-md hover:bg-gray-100 transition cursor-pointer">
          </div>

          <Button
            text="Add bill of lading"
            onClick={handleCreateDocument}
            backgroundColor="#223688"
            textColor="white"
            fontSize="14px"
            padding="10px 18px"
            borderRadius="24px"
          />
        </div>
      </div>

      {/* Tab Content Area */}
      <div className="flex-1 mt-fluid-2  min-h-0 flex flex-col scrollbar-hide">
        <Tabs
          key={tabs.length}
          tabs={tabs.map((tab) => tab.name)}
          tabContents={tabs.map((tab) => (
            defaultTabContents[tab.id] || (
              <NewDocumentUpload
                key={tab.id}
                title={tab.name}
                setActiveTab={(payload) => dispatch(setActiveTab(payload))}
                tabs={tabs}
              />
            )
          ))}
          activeTab={activeTab}
          onTabChange={(index) => dispatch(setActiveTab({ index }))}
          isDeletable={tabs.map((_, index) => index >= defaultTabsCount)}
          onRemoveTab={handleRemoveTab}
        />
      </div>
    </div>
  );
};

export default DocumentUploaderDetails;
